import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logger } from '@/lib/logger';

const FP_KEY = 'fh-device-fp';
const REGISTER_INTERVAL_MS = 5 * 60 * 1000; // 5 min

function getOrCreateFingerprint(): string {
  try {
    const cur = localStorage.getItem(FP_KEY);
    if (cur && cur.length > 8) return cur;
    const rnd = (crypto as any)?.randomUUID?.() ||
      Math.random().toString(36).slice(2) + Date.now().toString(36);
    localStorage.setItem(FP_KEY, rnd);
    return rnd;
  } catch {
    return 'anon-' + Math.random().toString(36).slice(2);
  }
}

export interface SessionGuardState {
  /** Set when this device's session was forcibly revoked (other login). */
  revoked: boolean;
  /** Set when there's an unacknowledged multi-location warning. */
  warningEvent: { id: string; details: any } | null;
  acknowledgeWarning: () => Promise<void>;
}

export function useSessionGuard(): SessionGuardState {
  const { user, signOut } = useAuth();
  const [revoked, setRevoked] = useState(false);
  const [warningEvent, setWarningEvent] = useState<SessionGuardState['warningEvent']>(null);
  const sessionIdRef = useRef<string | null>(null);
  const lastRegisterRef = useRef<number>(0);

  const register = useCallback(async () => {
    if (!user) return;
    const now = Date.now();
    if (now - lastRegisterRef.current < 30_000) return; // debounce
    lastRegisterRef.current = now;
    try {
      const { data, error } = await supabase.functions.invoke('register-session', {
        body: {
          device_fingerprint: getOrCreateFingerprint(),
          user_agent: navigator.userAgent,
          action: 'register',
        },
      });
      if (error) {
        logger.log('[SessionGuard] register error', error.message);
        return;
      }
      sessionIdRef.current = (data as any)?.session_id ?? null;
    } catch (e) {
      logger.log('[SessionGuard] register threw', e);
    }
  }, [user]);

  // Check for unacknowledged warnings in last 24h
  const loadWarning = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('session_abuse_events')
      .select('id,details,created_at,acknowledged_at,kind')
      .eq('user_id', user.id)
      .eq('kind', 'multi_ip_warning')
      .is('acknowledged_at', null)
      .gte('created_at', new Date(Date.now() - 24 * 36e5).toISOString())
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (data) setWarningEvent({ id: (data as any).id, details: (data as any).details });
  }, [user]);

  const acknowledgeWarning = useCallback(async () => {
    if (!warningEvent) return;
    await supabase
      .from('session_abuse_events')
      .update({ acknowledged_at: new Date().toISOString() })
      .eq('id', warningEvent.id);
    setWarningEvent(null);
  }, [warningEvent]);

  useEffect(() => {
    if (!user) {
      setRevoked(false);
      setWarningEvent(null);
      sessionIdRef.current = null;
      return;
    }

    register();
    loadWarning();

    const interval = setInterval(register, REGISTER_INTERVAL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') register();
    };
    document.addEventListener('visibilitychange', onVis);

    // Realtime: listen for revocation of MY active sessions
    const channel = supabase
      .channel(`user-sessions-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_sessions',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as any;
          if (!row) return;
          // Only react when MY session got revoked (and not by my own signOut)
          if (
            sessionIdRef.current &&
            row.id === sessionIdRef.current &&
            row.revoked_at &&
            row.revoke_reason !== 'signed_out'
          ) {
            setRevoked(true);
            // sign out locally; modal stays visible (handled by app)
            void signOut();
          }
        },
      )
      .subscribe();

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVis);
      supabase.removeChannel(channel);
    };
  }, [user?.id, register, loadWarning, signOut]);

  return { revoked, warningEvent, acknowledgeWarning };
}